/// <reference types="google.maps" />
// Google Maps JS API loader (singleton) + helpers
// Uses the Lovable-managed referrer-restricted browser key.


let loaderPromise: Promise<typeof google> | null = null;

export function loadGoogleMaps(): Promise<typeof google> {
  if (typeof window === 'undefined') return Promise.reject(new Error('SSR'));
  if ((window as any).google?.maps?.places) {
    return Promise.resolve((window as any).google);
  }
  if (loaderPromise) return loaderPromise;

  const key = (import.meta.env.VITE_CUSTOM_GOOGLE_MAPS_BROWSER_KEY ||
    import.meta.env.VITE_LOVABLE_CONNECTOR_GOOGLE_MAPS_BROWSER_KEY) as string | undefined;
  const channel = import.meta.env.VITE_LOVABLE_CONNECTOR_GOOGLE_MAPS_TRACKING_ID as string | undefined;
  if (!key) return Promise.reject(new Error('Google Maps browser key missing'));

  loaderPromise = new Promise((resolve, reject) => {
    (window as any).__lvbGmapsReady = () => resolve((window as any).google);
    const script = document.createElement('script');
    const params = new URLSearchParams({
      key,
      libraries: 'places',
      loading: 'async',
      callback: '__lvbGmapsReady',
      v: 'weekly',
    });
    if (channel) params.set('channel', channel);
    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
    script.async = true;
    script.onerror = () => {
      loaderPromise = null;
      reject(new Error('Failed to load Google Maps'));
    };
    document.head.appendChild(script);
  });
  return loaderPromise;
}

export interface SelectedPlace {
  description: string;
  placeId: string;
  isSalzburg: boolean;
}

// Salzburg city center bias (~5km radius for autocomplete biasing)
const SALZBURG_CENTER = { lat: 47.8095, lng: 13.0550 };

export async function fetchSuggestions(input: string, sessionToken?: any) {
  const g = await loadGoogleMaps();
  const { AutocompleteSuggestion, AutocompleteSessionToken } =
    await g.maps.importLibrary('places') as google.maps.PlacesLibrary;
  const token = sessionToken || new AutocompleteSessionToken();
  const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
    input,
    sessionToken: token,
    locationBias: {
      center: SALZBURG_CENTER,
      radius: 60000,
    },
    includedRegionCodes: ['at', 'de'],
  } as any);
  return { suggestions, token };
}

export async function resolvePlace(placeId: string, sessionToken?: any): Promise<SelectedPlace> {
  const g = await loadGoogleMaps();
  const { Place } = await g.maps.importLibrary('places') as google.maps.PlacesLibrary;
  const place = new Place({ id: placeId });
  await place.fetchFields({ fields: ['displayName', 'formattedAddress', 'addressComponents'] });
  const components = (place.addressComponents || []) as any[];
  const isSalzburg = components.some(c => {
    const types = c.types || [];
    const name = (c.longText || c.shortText || '').toLowerCase();
    return (types.includes('locality') || types.includes('postal_town')) && name === 'salzburg';
  });
  return {
    description: place.formattedAddress || place.displayName || '',
    placeId,
    isSalzburg,
  };
}

export async function fetchDistanceKm(originPlaceId: string, destPlaceId: string): Promise<number> {
  const g = await loadGoogleMaps();
  const svc = new g.maps.DistanceMatrixService();
  const res = await svc.getDistanceMatrix({
    origins: [{ placeId: originPlaceId } as any],
    destinations: [{ placeId: destPlaceId } as any],
    travelMode: g.maps.TravelMode.DRIVING,
  });
  const el = res.rows?.[0]?.elements?.[0];
  if (el && el.status === 'OK' && el.distance) {
    return el.distance.value / 1000;
  }
  return 0;
}
